import { useState, useMemo, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { DbPublication } from "@/types/database";
import { pubCategories } from "@/mocks/resourcesData";
import Icon from "@/components/feature/Icon";
import SmartImage from "@/components/feature/SmartImage";

type CategoryKey = string;

export default function PublicationsGrid() {
  const [activeCategory, setActiveCategory] = useState<CategoryKey>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [publications, setPublications] = useState<DbPublication[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPublications() {
      const { data, error } = await supabase
        .from("publications")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("id", { ascending: true });
      if (!error && data) {
        setPublications(data as DbPublication[]);
      }
      setLoading(false);
    }
    fetchPublications();
  }, []);

  const filtered = useMemo(() => {
    return publications.filter((p) => {
      const matchCategory = activeCategory === "all" || p.category === activeCategory;
      const matchSearch =
        !searchQuery ||
        p.title.includes(searchQuery) ||
        (p.summary && p.summary.includes(searchQuery));
      return matchCategory && matchSearch;
    });
  }, [activeCategory, searchQuery, publications]);

  const getFileIcon = (type: string | null) => {
    switch (type) {
      case "PDF":
        return "ri-file-pdf-line";
      case "PPT":
        return "ri-file-ppt-line";
      default:
        return "ri-file-text-line";
    }
  };

  const activeClass =
    "bg-navy-950 text-white shadow-lg shadow-navy-950/30 ring-2 ring-white/20";
  const inactiveClass =
    "bg-silver-50 text-silver-600 hover:bg-silver-100 border border-silver-200";

  return (
    <section id="publications" className="pt-16 pb-24 md:pt-20 md:pb-32 bg-white">
      <div className="px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-3">
              Publications & Downloads
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-950 leading-tight">
              出版品與下載
            </h2>
          </div>
          <div className="relative w-full md:w-80">
            <Icon className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-silver-400" />
            <input
              type="text"
              placeholder="搜尋出版品..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-silver-50 border border-silver-200 rounded-full text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-12 md:mb-16">
          {pubCategories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                activeCategory === cat.key ? activeClass : inactiveClass
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="text-center py-20">
            <Icon className="ri-loader-4-line animate-spin text-2xl text-silver-400 inline-block mb-2" />
            <p className="text-sm text-silver-400">載入出版品中...</p>
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((pub) => (
              <article
                key={pub.id}
                className="group bg-silver-50 rounded-3xl overflow-hidden border border-silver-200 hover:border-navy-200 hover:shadow-xl transition-all duration-500 flex flex-col"
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-navy-100">
                  <SmartImage
                    src={pub.image || ""}
                    alt={pub.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                    fallbackText={pub.category}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950/50 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="bg-white/90 backdrop-blur-sm text-navy-950 text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1">
                      <Icon className={getFileIcon(pub.file_type)} />
                      {pub.file_type || "檔案"}
                    </span>
                    {pub.is_new && (
                      <span className="bg-navy-950 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                        NEW
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-6 md:p-7 flex flex-col flex-grow">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium bg-navy-50 text-navy-700 px-2.5 py-1 rounded-full">
                      {pub.category}
                    </span>
                    <span className="text-xs text-silver-400">{pub.date}</span>
                  </div>
                  <h3 className="text-base md:text-lg font-semibold text-navy-950 mb-2 group-hover:text-navy-700 transition-colors leading-snug">
                    {pub.title}
                  </h3>
                  <p className="text-sm text-silver-500 leading-relaxed line-clamp-3 mb-5 flex-grow">
                    {pub.summary || ""}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-silver-400 mb-5">
                    <span className="flex items-center gap-1">
                      <Icon className="ri-file-list-line" />
                      {pub.pages || "-"} 頁
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon className="ri-hard-drive-2-line" />
                      {pub.file_size || "-"}
                    </span>
                  </div>
                  {pub.file_url ? (
                    <a
                      href={pub.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 py-3 bg-navy-950 text-white rounded-full text-sm font-medium hover:bg-navy-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <Icon className="ri-download-cloud-2-line" />
                      下載 {pub.file_type || ""}
                    </a>
                  ) : (
                    <span className="w-full inline-flex items-center justify-center gap-2 py-3 bg-silver-200 text-silver-500 rounded-full text-sm font-medium whitespace-nowrap cursor-not-allowed">
                      <Icon className="ri-time-line" />
                      即將開放
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Icon className="ri-file-search-line text-5xl text-silver-300 mb-4" />
            <p className="text-silver-500 text-lg mb-2">沒有符合條件的資源</p>
            <p className="text-silver-400 text-sm">請嘗試其他篩選條件或搜尋關鍵字</p>
          </div>
        )}
      </div>
    </section>
  );
}